/** Normalizes track-and-trace report payloads that expose line data via `asns` or flat `rows` / `data`. */

export type TransferAsnRow = {
  key: string;
  asn: string;
  identityId: string;
  sku: string;
  description: string;
  qty: number;
  status: string;
  transferredFromSiteId: string;
  transferredFromZoneId: string;
  transferredToSiteId: string;
  transferredToZoneId: string;
  transferredFromSite: string;
  transferredFromZone: string;
  transferredToSite: string;
  transferredToZone: string;
  transferredFrom: string;
  transferredTo: string;
  dateTime: string;
};

export type ReceiveAsnRow = {
  key: string;
  asn: string;
  sku: string;
  description: string;
  dateTime: string;
  expectedQty: number;
  receivedQty: number;
  missingQty: number;
  unexpectedQty: number;
  identityId: string;
  receivedProcessSession: string;
  siteId: string;
};

/** Line-level receive row with site context (site receive / drill-down report). */
export type SiteReceiveReportRow = {
  key: string;
  asn: string;
  siteName: string;
  zoneName: string;
  userName: string;
  sku: string;
  description: string;
  expectedQty: number;
  receivedQty: number;
  missingQty: number;
  unexpectedQty: number;
  identityId: string;
  siteId: string;
  zoneId: string;
};

export function asnKeyFromRow(row: Record<string, unknown>): string {
  const v = row.asn ?? row.asnNumber ?? row.asnId ?? row.ASN ?? row.asnCode;
  const s = String(v ?? "").trim();
  return s || "(No ASN)";
}

/** Flattens `asns` entries (and optional nested line arrays) into line-level records for grouping. */
export function collectReportLineItems(reportResult: unknown): Record<string, unknown>[] {
  const r = reportResult as Record<string, unknown> | null | undefined;
  if (!r) return [];
  const asns = r.asns;
  // # Command Line: representing data in pages according to user selected in Limit
  if (Array.isArray(asns) && asns.length > 0) {
    const out: Record<string, unknown>[] = [];
    for (const entry of asns) {
      if (!entry || typeof entry !== "object") continue;
      const o = entry as Record<string, unknown>;
      const nested = o.lines ?? o.items ?? o.skus ?? o.details ?? o.rows ?? o.lineItems;
      const parentAsn = o.asn ?? o.asnNumber ?? o.asnId ?? o.ASN;
      if (Array.isArray(nested) && nested.length > 0) {
        for (const line of nested) {
          if (line && typeof line === "object") {
            const lineObj = line as Record<string, unknown>;
            out.push({
              ...lineObj,
              asn: lineObj.asn ?? lineObj.asnNumber ?? parentAsn,
            });
          }
        }
      } else {
        out.push(o);
      }
    }
    return out;
  }
  const rows = r.rows ?? r.data;
  return Array.isArray(rows) ? (rows as Record<string, unknown>[]) : [];
}

function pickLatestDate(dates: string[]): string {
  const sorted = dates.filter(Boolean).sort();
  return sorted[sorted.length - 1] ?? "";
}

function pickTransferLocationId(item: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = item[key];
    if (typeof value === "string" || typeof value === "number") {
      const s = String(value).trim();
      if (s) return s;
    }
    if (value && typeof value === "object") {
      const nestedId = String((value as Record<string, unknown>).id ?? "").trim();
      if (nestedId) return nestedId;
    }
  }
  return "";
}

function pickRecentSiteName(item: Record<string, unknown>, index: number): string {
  const recentSites = item.recentSites;
  if (!Array.isArray(recentSites)) return "";
  const recent = recentSites[index];
  if (!recent || typeof recent !== "object") return "";
  return String((recent as Record<string, unknown>).siteName ?? "").trim();
}

function pickRecentZoneName(item: Record<string, unknown>, index: number): string {
  const recentSites = item.recentSites;
  if (!Array.isArray(recentSites)) return "";
  const recent = recentSites[index];
  if (!recent || typeof recent !== "object") return "";
  return String((recent as Record<string, unknown>).zoneName ?? "").trim();
}

function mapAsnDocumentToTransferRow(item: Record<string, unknown>, index: number): TransferAsnRow {
  return {
    key: String(item.key ?? item._id ?? item.id ?? item.asn ?? item.asnNumber ?? index),
    asn: String(item.asn ?? item.asnNumber ?? item.asnId ?? ""),
    identityId: String(item.identityId ?? item.diId ?? item.primaryId ?? item.id ?? item._id ?? ""),
    sku: String(item.sku ?? item.productUPC ?? ""),
    description: String(item.description ?? item.productDescription ?? ""),
    qty: Number(item.qty ?? item.count ?? item.totalCount ?? 0),
    status: String(item.status ?? ""),
    transferredFromSiteId: pickTransferLocationId(item, [
      "transferredFromSiteId",
      "sourceSiteId",
      "fromSiteId",
      "siteFromId",
      "transferredFromLocation",
      "sourceSite",
      "fromSite",
      "siteFrom",
    ]),
    transferredFromZoneId: pickTransferLocationId(item, [
      "transferredFromZoneId",
      "transferredFromZone",
      "sourceZoneId",
      "fromZoneId",
      "zoneFromId",
      "zone",
      "sourceZone",
      "fromZone",
      "zoneFrom",
    ]),
    transferredToSiteId: pickTransferLocationId(item, [
      "transferredToSiteId",
      "destinationSiteId",
      "toSiteId",
      "siteToId",
      "transferredToLocation",
      "destinationSite",
      "toSite",
      "siteTo",
    ]),
    transferredToZoneId: pickTransferLocationId(item, [
      "transferredToZoneId",
      "transferredToZone",
      "destinationZoneId",
      "toZoneId",
      "zoneToId",
      "zone",
      "destinationZone",
      "toZone",
      "zoneTo",
    ]),
    transferredFromSite: String(
      item.transferredFromSite ??
        item.sourceSite ??
        item.fromSite ??
        item.siteFrom ??
        pickRecentSiteName(item, 0) ??
        ""
    ),
    transferredFromZone: String(
      item.transferredFromZone ??
        item.sourceZone ??
        item.fromZone ??
        item.zoneFrom ??
        pickRecentZoneName(item, 0) ??
        ""
    ),
    transferredToSite: String(
      item.transferredToSite ??
        item.destinationSite ??
        item.toSite ??
        item.siteTo ??
        item.siteName ??
        pickRecentSiteName(item, 1) ??
        ""
    ),
    transferredToZone: String(
      item.transferredToZone ??
        item.destinationZone ??
        item.toZone ??
        item.zoneTo ??
        item.zoneName ??
        pickRecentZoneName(item, 1) ??
        ""
    ),
    transferredFrom: String(
      item.transferredFrom ?? item.transferredFromLocation ?? item.fromLocation ?? item.sourceLocation ?? ""
    ),
    transferredTo: String(
      item.transferredTo ?? item.transferredToLocation ?? item.toLocation ?? item.destinationLocation ?? ""
    ),
    dateTime: String(item.dateTime ?? item.lastUpdatedAt ?? item.updatedAt ?? item.operationTime ?? item.createdAt ?? ""),
  };
}

function mapAsnDocumentToReceiveRow(item: Record<string, unknown>, index: number): ReceiveAsnRow {
  return {
    key: String(item.key ?? item._id ?? item.id ?? item.asn ?? item.asnNumber ?? index),
    asn: String(item.asn ?? item.asnNumber ?? item.asnId ?? ""),
    sku: String(item.sku ?? item.productUPC ?? ""),
    description: String(item.description ?? item.productDescription ?? ""),
    dateTime: String(item.updatedAt ?? item.lastUpdatedAt ?? item.operationTime ?? item.createdAt ?? ""),
    expectedQty: Number(item.expectedQty ?? item.expected ?? item.expectedCount ?? 0),
    receivedQty: Number(item.receivedQty ?? item.received ?? item.receivedCount ?? 0),
    missingQty: Number(item.missingQty ?? item.missing ?? item.missingCount ?? 0),
    unexpectedQty: Number(item.unexpectedQty ?? item.unexpected ?? item.unexpectedCount ?? 0),
    identityId: String(item.identityId ?? item.diId ?? item.primaryId ?? ""),
    receivedProcessSession: String(item.receivedProcessSession ?? item.processSession ?? ""),
    siteId: String(item.siteId ?? ""),
  };
}

export function buildTransferReportRows(reportResult: unknown): TransferAsnRow[] {
  const r = reportResult as Record<string, unknown> | null | undefined;
  if (!r) return [];
  if (Array.isArray(r.asns) && r.asns.length > 0) {
    const list = r.asns as Record<string, unknown>[];
    const hasNestedLines = list.some((item) =>
      Array.isArray(item.lines ?? item.items ?? item.skus ?? item.details ?? item.rows ?? item.lineItems)
    );
    if (!hasNestedLines) {
      return list.map((item, index) => mapAsnDocumentToTransferRow(item, index));
    }
  }
  return buildTransferRowsGroupedByAsn(collectReportLineItems(r));
}

export function buildReceiveReportRows(reportResult: unknown): ReceiveAsnRow[] {
  const r = reportResult as Record<string, unknown> | null | undefined;
  if (!r) return [];
  if (Array.isArray(r.asns) && r.asns.length > 0) {
    const list = r.asns as Record<string, unknown>[];
    const hasNestedLines = list.some((item) =>
      Array.isArray(item.lines ?? item.items ?? item.skus ?? item.details ?? item.rows ?? item.lineItems)
    );
    if (!hasNestedLines) {
      return list.map((item, index) => mapAsnDocumentToReceiveRow(item, index));
    }
  }
  return buildReceiveRowsGroupedByAsn(collectReportLineItems(r));
}

function mapRecordToSiteReceiveRow(row: Record<string, unknown>, index: number): SiteReceiveReportRow {
  const siteName = String(
    row.siteName ??
      row.site ??
      row.siteLabel ??
      row.locationName ??
      row.transferredToLocation ??
      row.toLocation ??
      row.destinationLocation ??
      ""
  ).trim();
  const zoneName = String(row.zoneName ?? row.zone ?? "").trim();
  const userName = String(row.userName ?? row.user ?? row.username ?? "").trim();
  return {
    key: String(row.key ?? row._id ?? row.id ?? `${row.asn ?? "asn"}-${row.sku ?? "sku"}-${index}`),
    asn: String(row.asn ?? row.asnNumber ?? row.asnId ?? ""),
    siteName,
    zoneName,
    userName,
    sku: String(row.sku ?? row.productUPC ?? ""),
    description: String(row.description ?? row.productDescription ?? ""),
    expectedQty: Number(row.expectedQty ?? row.expected ?? row.expectedCount ?? 0),
    receivedQty: Number(row.receivedQty ?? row.received ?? row.receivedCount ?? 0),
    missingQty: Number(row.missingQty ?? row.missing ?? row.missingCount ?? 0),
    unexpectedQty: Number(row.unexpectedQty ?? row.unexpected ?? row.unexpectedCount ?? 0),
    identityId: String(row.identityId ?? row.diId ?? row.primaryId ?? ""),
    siteId: String(row.siteId ?? row.sourceSiteId ?? row.fromSiteId ?? ""),
    zoneId: String(row.zoneId ?? row.sourceZoneId ?? row.fromZoneId ?? ""),
  };
}

/** Flattens the same receive payloads as `buildReceiveReportRows` and adds site/zone/user columns per line. */
export function buildSiteReceiveReportRows(reportResult: unknown): SiteReceiveReportRow[] {
  const r = reportResult as Record<string, unknown> | null | undefined;
  if (!r) return [];

  const fromLines = collectReportLineItems(r);
  if (fromLines.length > 0) {
    return fromLines.map((row, index) => mapRecordToSiteReceiveRow(row, index));
  }

  const rows = r.rows ?? r.data;
  if (Array.isArray(rows) && rows.length > 0) {
    return (rows as Record<string, unknown>[]).map((row, index) => mapRecordToSiteReceiveRow(row, index));
  }

  return [];
}

function buildTransferRowsGroupedByAsn(items: Record<string, unknown>[]): TransferAsnRow[] {
  type Acc = {
    asn: string;
    skus: Set<string>;
    descriptions: Set<string>;
    qty: number;
    fromSiteIds: Set<string>;
    fromZoneIds: Set<string>;
    toSiteIds: Set<string>;
    toZoneIds: Set<string>;
    fromSite: Set<string>;
    fromZone: Set<string>;
    toSite: Set<string>;
    toZone: Set<string>;
    from: Set<string>;
    to: Set<string>;
    dates: string[];
    identityIds: Set<string>;
    statuses: Set<string>;
  };
  const map = new Map<string, Acc>();
  items.forEach((row) => {
    const asn = asnKeyFromRow(row);
    if (!map.has(asn)) {
      map.set(asn, {
        asn,
        skus: new Set(),
        descriptions: new Set(),
        qty: 0,
        statuses: new Set(),
        fromSiteIds: new Set(),
        fromZoneIds: new Set(),
        toSiteIds: new Set(),
        toZoneIds: new Set(),
        fromSite: new Set(),
        fromZone: new Set(),
        toSite: new Set(),
        toZone: new Set(),
        from: new Set(),
        to: new Set(),
        dates: [],
        identityIds: new Set(),
      });
    }
    const g = map.get(asn)!;
    const sku = String(row.sku ?? row.productUPC ?? "").trim();
    const status = String(row.status ?? row.transferStatus ?? "").trim();
    if (status) g.statuses.add(status);
    if (sku) g.skus.add(sku);
    const desc = String(row.description ?? row.productDescription ?? "").trim();
    if (desc) g.descriptions.add(desc);
    g.qty += Number(row.qty ?? row.count ?? row.totalCount ?? 0);
    const fromSiteId = pickTransferLocationId(row, [
      "transferredFromSiteId",
      "sourceSiteId",
      "fromSiteId",
      "siteFromId",
      "transferredFromLocation",
      "sourceSite",
      "fromSite",
      "siteFrom",
    ]);
    if (fromSiteId) g.fromSiteIds.add(fromSiteId);
    const fromZoneId = pickTransferLocationId(row, [
      "transferredFromZoneId",
      "transferredFromZone",
      "sourceZoneId",
      "fromZoneId",
      "zoneFromId",
      "zone",
      "sourceZone",
      "fromZone",
      "zoneFrom",
    ]);
    if (fromZoneId) g.fromZoneIds.add(fromZoneId);
    const toSiteId = pickTransferLocationId(row, [
      "transferredToSiteId",
      "destinationSiteId",
      "toSiteId",
      "siteToId",
      "transferredToLocation",
      "destinationSite",
      "toSite",
      "siteTo",
    ]);
    if (toSiteId) g.toSiteIds.add(toSiteId);
    const toZoneId = pickTransferLocationId(row, [
      "transferredToZoneId",
      "transferredToZone",
      "destinationZoneId",
      "toZoneId",
      "zoneToId",
      "zone",
      "destinationZone",
      "toZone",
      "zoneTo",
    ]);
    if (toZoneId) g.toZoneIds.add(toZoneId);
    const fromSite = String(
      row.transferredFromSite ??
        row.sourceSite ??
        row.fromSite ??
        row.siteFrom ??
        pickRecentSiteName(row, 0) ??
        ""
    ).trim();
    if (fromSite) g.fromSite.add(fromSite);
    const fromZone = String(
      row.transferredFromZone ??
        row.sourceZone ??
        row.fromZone ??
        row.zoneFrom ??
        pickRecentZoneName(row, 0) ??
        ""
    ).trim();
    if (fromZone) g.fromZone.add(fromZone);
    const toSite = String(
      row.transferredToSite ??
        row.destinationSite ??
        row.toSite ??
        row.siteTo ??
        row.siteName ??
        pickRecentSiteName(row, 1) ??
        ""
    ).trim();
    if (toSite) g.toSite.add(toSite);
    const toZone = String(
      row.transferredToZone ??
        row.destinationZone ??
        row.toZone ??
        row.zoneTo ??
        row.zoneName ??
        pickRecentZoneName(row, 1) ??
        ""
    ).trim();
    if (toZone) g.toZone.add(toZone);
    const tf = String(
      row.transferredFrom ?? row.transferredFromLocation ?? row.fromLocation ?? row.sourceLocation ?? ""
    ).trim();
    if (tf) g.from.add(tf);
    const tt = String(
      row.transferredTo ?? row.transferredToLocation ?? row.toLocation ?? row.destinationLocation ?? ""
    ).trim();
    if (tt) g.to.add(tt);
    const dt = String(row.dateTime ?? row.lastUpdatedAt ?? row.updatedAt ?? row.operationTime ?? row.createdAt ?? "").trim();
    if (dt) g.dates.push(dt);
    const id = String(row.identityId ?? row.diId ?? row.primaryId ?? row.id ?? row._id ?? "").trim();
    if (id) g.identityIds.add(id);
  });
  return Array.from(map.entries()).map(([asnLabel, g], i) => {
    const skusArr = Array.from(g.skus);
    const descArr = Array.from(g.descriptions);
    const idArr = Array.from(g.identityIds);
    return {
      key: `asn-${asnLabel}-${i}`,
      asn: g.asn === "(No ASN)" ? asnLabel : g.asn,
      identityId: idArr.length === 1 ? idArr[0] : "",
      sku: skusArr.length === 0 ? "" : skusArr.length === 1 ? skusArr[0] : skusArr.sort().join(", "),
      description: descArr.length === 0 ? "" : descArr.length === 1 ? descArr[0] : descArr.join("; "),
      qty: g.qty,
      status: Array.from(g.statuses).sort().join(", "),
      transferredFromSiteId: Array.from(g.fromSiteIds).sort().join(", "),
      transferredFromZoneId: Array.from(g.fromZoneIds).sort().join(", "),
      transferredToSiteId: Array.from(g.toSiteIds).sort().join(", "),
      transferredToZoneId: Array.from(g.toZoneIds).sort().join(", "),
      transferredFromSite: Array.from(g.fromSite).sort().join(", "),
      transferredFromZone: Array.from(g.fromZone).sort().join(", "),
      transferredToSite: Array.from(g.toSite).sort().join(", "),
      transferredToZone: Array.from(g.toZone).sort().join(", "),
      transferredFrom: Array.from(g.from).sort().join(", "),
      transferredTo: Array.from(g.to).sort().join(", "),
      dateTime: pickLatestDate(g.dates),
    };
  }).sort((a, b) => a.asn.localeCompare(b.asn));
}

function buildReceiveRowsGroupedByAsn(items: Record<string, unknown>[]): ReceiveAsnRow[] {
  const isBlankAsn = (value: string) => {
    const v = value.trim().toLowerCase();
    return !v || v === "(no asn)" || v === "null" || v === "undefined";
  };
  const resolveReceiveGroupKey = (row: Record<string, unknown>, index: number) => {
    const asn = asnKeyFromRow(row);
    if (!isBlankAsn(asn)) return asn;
    // Keep blind receives distinct. Different sessions/sites/zones should not collapse.
    const blindScope = String(
      row.receivedProcessSession ??
        row.processSession ??
        row.siteId ??
        row.zoneId ??
        row.lastUpdatedAt ??
        row.createdAt ??
        index
    ).trim();
    return `BLIND::${blindScope || index}`;
  };
  type Acc = {
    groupKey: string;
    asn: string;
    skus: Set<string>;
    descriptions: Set<string>;
    dates: string[];
    expectedQty: number;
    receivedQty: number;
    missingQty: number;
    unexpectedQty: number;
    identityIds: Set<string>;
    receivedProcessSession: string;
    siteId: string;
  };
  const map = new Map<string, Acc>();
  items.forEach((row, index) => {
    const groupKey = resolveReceiveGroupKey(row, index);
    const rawAsn = asnKeyFromRow(row);
    if (!map.has(groupKey)) {
      map.set(groupKey, {
        groupKey,
        asn: isBlankAsn(rawAsn) ? "BLIND" : rawAsn,
        skus: new Set(),
        descriptions: new Set(),
        dates: [],
        expectedQty: 0,
        receivedQty: 0,
        missingQty: 0,
        unexpectedQty: 0,
        identityIds: new Set(),
        receivedProcessSession: String(row.receivedProcessSession ?? row.processSession ?? "").trim(),
        siteId: String(row.siteId ?? "").trim(),
      });
    }
    const g = map.get(groupKey)!;
    const sku = String(row.sku ?? row.productUPC ?? "").trim();
    if (sku) g.skus.add(sku);
    const desc = String(row.description ?? row.productDescription ?? "").trim();
    if (desc) g.descriptions.add(desc);
    const dt = String(row.updatedAt ?? row.lastUpdatedAt ?? row.operationTime ?? row.createdAt ?? "").trim();
    if (dt) g.dates.push(dt);
    g.expectedQty += Number(row.expectedQty ?? row.expected ?? row.expectedCount ?? 0);
    g.receivedQty += Number(row.receivedQty ?? row.received ?? row.receivedCount ?? 0);
    g.missingQty += Number(row.missingQty ?? row.missing ?? row.missingCount ?? 0);
    g.unexpectedQty += Number(row.unexpectedQty ?? row.unexpected ?? row.unexpectedCount ?? 0);
    const id = String(row.identityId ?? row.diId ?? row.primaryId ?? "").trim();
    if (id) g.identityIds.add(id);
  });
  return Array.from(map.entries()).map(([groupKey, g], i) => {
    const skusArr = Array.from(g.skus);
    const descArr = Array.from(g.descriptions);
    const idArr = Array.from(g.identityIds);
    return {
      key: `asn-${groupKey}-${i}`,
      asn: g.asn,
      sku: skusArr.length === 0 ? "" : skusArr.length === 1 ? skusArr[0] : skusArr.sort().join(", "),
      description: descArr.length === 0 ? "" : descArr.length === 1 ? descArr[0] : descArr.join("; "),
      dateTime: pickLatestDate(g.dates),
      expectedQty: g.expectedQty,
      receivedQty: g.receivedQty,
      missingQty: g.missingQty,
      unexpectedQty: g.unexpectedQty,
      identityId: idArr.length === 1 ? idArr[0] : "",
      receivedProcessSession: g.receivedProcessSession,
      siteId: g.siteId,
    };
  }).sort((a, b) => a.asn.localeCompare(b.asn));
}